import { useEffect, useState } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import { useMaintenanceCampaignBySlug } from '../../hooks/useMaintenanceInfaq';
import { maintenanceInfaqService } from '../../services/maintenance-infaq.service';
import LoadingSpinner from '../../components/LoadingSpinner';
import { PhoneInput } from '../../components/ui';

const PRESETS = [10, 25, 50, 100];

export default function MaintenanceCampaignDetailPage() {
  const { slug } = useParams<{ slug: string }>();
  const [params] = useSearchParams();
  const { data: campaign, isLoading, error } = useMaintenanceCampaignBySlug(slug);

  const [amount, setAmount] = useState<number>(25);
  const [form, setForm] = useState({
    donorName: '',
    donorEmail: '',
    donorPhone: '',
    isAnonymous: false,
    prayerRequest: '',
  });
  const [submitting, setSubmitting] = useState(false);

  const redirectStatus = params.get('status');
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }
  if (error || !campaign) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-neutral-50">
        <div className="text-center">
          <h1 className="mb-2 text-2xl font-bold text-neutral-900">Campaign not found</h1>
          <Link to="/infaq" className="text-primary-700 hover:underline">
            Back to infaq
          </Link>
        </div>
      </div>
    );
  }

  const progressPct =
    campaign.targetAmount > 0
      ? Math.min(100, Math.round((campaign.raisedAmount / campaign.targetAmount) * 100))
      : 0;

  const handleDonate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!amount || amount <= 0) return;
    try {
      setSubmitting(true);
      const res = await maintenanceInfaqService.donate({
        maintenanceInfaqId: campaign.id,
        donorName: form.donorName,
        donorEmail: form.donorEmail,
        donorPhone: form.donorPhone,
        amount,
        isAnonymous: form.isAnonymous,
        prayerRequest: form.prayerRequest || undefined,
      });
      const url = res?.redipayDetails?.payment_url;
      if (url) {
        window.location.href = url;
      } else {
        alert('Payment URL not returned by gateway.');
      }
    } catch (err: any) {
      alert(err?.response?.data?.message || 'Failed to initiate donation.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-neutral-50">
      <div className="mx-auto max-w-5xl px-4 py-8">
        <Link
          to="/infaq"
          className="text-sm text-primary-700 hover:underline"
        >
          ← Back to infaq
        </Link>

        {redirectStatus === 'success' && (
          <div className="mt-4 rounded-md border border-emerald-300 bg-emerald-50 p-4 text-emerald-900">
            <strong>Thank you!</strong> Your donation was received successfully.
          </div>
        )}
        {redirectStatus === 'failed' && (
          <div className="mt-4 rounded-md border border-red-300 bg-red-50 p-4 text-red-900">
            <strong>Payment failed.</strong> Please try again.
          </div>
        )}

        <div className="mt-4 grid gap-6 lg:grid-cols-5">
          <div className="lg:col-span-3 space-y-4">
            <div className="rounded-lg bg-white p-6 shadow-sm">
              <span className="inline-block rounded-full bg-amber-100 px-2 py-0.5 text-xs font-semibold text-amber-800">
                Maintenance infaq
              </span>
              <h1 className="mt-2 text-3xl font-bold text-neutral-900">{campaign.title}</h1>
              <p className="mt-1 text-sm text-neutral-600">
                by {campaign.providerName}
              </p>
              <p className="mt-1 text-sm text-neutral-700">{campaign.reason}</p>

              <div className="mt-4">
                <div className="flex items-end justify-between">
                  <div className="text-2xl font-bold tabular-nums">
                    RM {campaign.raisedAmount.toFixed(2)}
                  </div>
                  <div className="text-sm text-neutral-500">
                    of RM {campaign.targetAmount.toFixed(2)}
                  </div>
                </div>
                <div className="mt-2 h-2 w-full overflow-hidden rounded-full bg-neutral-100">
                  <div
                    className="h-full bg-primary-600"
                    style={{ width: `${progressPct}%` }}
                  />
                </div>
                <div className="mt-2 flex justify-between text-xs text-neutral-500">
                  <span>{progressPct}% funded</span>
                  <span>
                    {campaign.donorCount} donor{campaign.donorCount === 1 ? '' : 's'}
                  </span>
                  {campaign.daysLeft !== null && (
                    <span>{campaign.daysLeft} day(s) left</span>
                  )}
                </div>
              </div>
            </div>

            <div className="rounded-lg bg-white p-6 shadow-sm">
              <h2 className="text-lg font-semibold text-neutral-900">About this campaign</h2>
              <p className="mt-2 whitespace-pre-wrap text-sm text-neutral-700">
                {campaign.description}
              </p>
              {campaign.damagePhotoUrls &&
                Array.isArray(campaign.damagePhotoUrls) &&
                (campaign.damagePhotoUrls as string[]).length > 0 && (
                  <div className="mt-4 grid grid-cols-2 gap-2 sm:grid-cols-3">
                    {(campaign.damagePhotoUrls as string[]).map((u, i) => (
                      <a
                        key={i}
                        href={u}
                        target="_blank"
                        rel="noreferrer"
                        className="overflow-hidden rounded-md border border-neutral-200"
                      >
                        <img
                          src={u}
                          alt={`damage-${i}`}
                          className="h-32 w-full object-cover"
                        />
                      </a>
                    ))}
                  </div>
                )}
              {campaign.tireChange && (
                <p className="mt-4 rounded-md bg-amber-50 p-2 text-xs text-amber-800">
                  Tire change / major part replacement flagged.
                </p>
              )}
            </div>

            {campaign.donations?.length > 0 && (
              <div className="rounded-lg bg-white p-6 shadow-sm">
                <h2 className="text-lg font-semibold text-neutral-900">Recent donors</h2>
                <ul className="mt-2 divide-y divide-neutral-100">
                  {campaign.donations.map((d, i) => (
                    <li key={i} className="flex justify-between py-2 text-sm">
                      <span className="font-medium text-neutral-800">
                        {d.isAnonymous ? 'Anonymous' : d.donorName}
                      </span>
                      <span className="tabular-nums text-neutral-700">
                        RM {Number(d.amount).toFixed(2)}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          <div className="lg:col-span-2">
            <form
              onSubmit={handleDonate}
              className="sticky top-4 space-y-4 rounded-lg bg-white p-6 shadow-sm"
            >
              <h2 className="text-lg font-semibold text-neutral-900">Donate now</h2>

              <div>
                <label className="block text-xs font-medium text-neutral-600">
                  Amount (RM)
                </label>
                <div className="mt-1 grid grid-cols-4 gap-1">
                  {PRESETS.map((p) => (
                    <button
                      key={p}
                      type="button"
                      onClick={() => setAmount(p)}
                      className={`rounded-md border px-2 py-1 text-sm ${
                        amount === p
                          ? 'border-primary-600 bg-primary-50 text-primary-700'
                          : 'border-neutral-300 text-neutral-700 hover:bg-neutral-50'
                      }`}
                    >
                      {p}
                    </button>
                  ))}
                </div>
                <input
                  type="number"
                  min={1}
                  step="0.01"
                  value={amount || ''}
                  onChange={(e) => setAmount(parseFloat(e.target.value) || 0)}
                  className="mt-2 w-full rounded-md border border-neutral-300 px-3 py-2 text-sm"
                  placeholder="Custom amount"
                  required
                />
              </div>

              <FormInput
                label="Name"
                value={form.donorName}
                onChange={(v) => setForm({ ...form, donorName: v })}
                required
              />
              <FormInput
                label="Email"
                type="email"
                value={form.donorEmail}
                onChange={(v) => setForm({ ...form, donorEmail: v })}
                required
              />
              <PhoneInput
                label="Phone"
                value={form.donorPhone}
                onChange={(v) => setForm({ ...form, donorPhone: v })}
                required
              />

              <div>
                <label className="block text-xs font-medium text-neutral-600">
                  Prayer request (optional)
                </label>
                <textarea
                  rows={2}
                  value={form.prayerRequest}
                  onChange={(e) => setForm({ ...form, prayerRequest: e.target.value })}
                  className="mt-1 w-full rounded-md border border-neutral-300 px-3 py-2 text-sm"
                />
              </div>

              <label className="flex items-center gap-2 text-sm text-neutral-700">
                <input
                  type="checkbox"
                  checked={form.isAnonymous}
                  onChange={(e) => setForm({ ...form, isAnonymous: e.target.checked })}
                  className="h-4 w-4 rounded border-neutral-300"
                />
                Donate anonymously
              </label>

              <button
                type="submit"
                disabled={submitting || amount <= 0}
                className="w-full rounded-md bg-primary-600 px-4 py-2 text-sm font-semibold text-white hover:bg-primary-700 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {submitting ? 'Processing…' : `Donate RM ${amount.toFixed(2)}`}
              </button>
              <p className="text-center text-xs text-neutral-500">
                You will be redirected to our secure payment gateway.
              </p>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}

function FormInput({
  label,
  value,
  onChange,
  type = 'text',
  required,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  type?: string;
  required?: boolean;
}) {
  return (
    <div>
      <label className="block text-xs font-medium text-neutral-600">
        {label} {required && <span className="text-red-600">*</span>}
      </label>
      <input
        type={type}
        value={value}
        required={required}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1 w-full rounded-md border border-neutral-300 px-3 py-2 text-sm"
      />
    </div>
  );
}
